import React, { useState } from 'react';
import styled from '@emotion/styled';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';
import PlusCircleIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/plus-circle.svg';
import { ImageSize } from '../GeneralItem';
import { useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';

const Wrapper = styled.div`
  width: 50%;
  padding: 24px;
  box-sizing: border-box;

  @media (orientation: landscape) {
    width: 33.33%;
  }
`;

const Inner = styled(TouchedWrapper)`
  background: rgb(255, 255, 255);
  border-radius: 40px;
  height: 408px;
  overflow: hidden;

  @media (orientation: landscape) {
    height: 360px;
  }
`;

const Image = styled.div<{ url: any; imageSize: ImageSize }>`
  width: 100%;
  height: ${({ imageSize }) => imageSize.height}px;
  background-image: url(${(props) => (props.url ? props.url : 'none')});
  background-size: cover;
  background-position: center;
  position: relative;

  @media (orientation: landscape) {
    height: 285px;
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
`;

const Title = styled.h3`
  color: rgb(33, 33, 36);
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;

  @media (orientation: landscape) {
    font-size: 22px;
    line-height: 30px;
  }
`;

const Price = styled.p`
  color: rgb(33, 33, 36);
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  line-height: 32px;
  text-align: right;

  @media (orientation: landscape) {
    font-size: 22px;
    line-height: 30px;
  }
`;

const OutOfStockOverlay = styled.div<{ imageSize: ImageSize }>`
  text-align: center;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(66, 66, 69, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(255, 255, 255);
  font-family: Eina01-SemiBold;
  font-size: 36px;
  line-height: 56px;
  font-weight: 600;

  @media (orientation: landscape) {
    font-size: 33px;
    line-height: 52px;
  }
`;

const AddToCartOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20;
  border-radius: 40px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  &.active {
    opacity: 1;
  }
`;

const AddedText = styled.div`
  color: var(--colorWhite);
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.85);
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InstantAddIndicator = styled.div`
  position: absolute;
  top: 16px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
`;

interface Props {
  onClick: () => void;
  isInstant: boolean;
  title: string;
  price: string;
  imageUrl: string;
  isUnavailable: boolean;
  imageSize: ImageSize;
}

const Item = ({ onClick, isInstant, title, price, imageUrl, isUnavailable, imageSize }: Props) => {
  const { i18n } = useI18n();
  const [isAdded, setIsAdded] = useState(false);
  const [animate, setAnimate] = useState(false);

  const instantClick = () => {
    if (!isAdded) {
      setIsAdded(true);
    }

    if (isInstant) {
      setAnimate(true);
      setTimeout(() => {
        setAnimate(false);
      }, 600);
    }

    onClick();
  };

  return (
    <Wrapper>
      <Inner
        onClick={instantClick}
        shadows={{
          pressed: '0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
          init: `0 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(0, 0, 0, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)`,
        }}>
        <AddToCartOverlay className={animate ? 'active' : ''}>
          <AddedText>{i18n('Express.Item.AddToCart')}</AddedText>
        </AddToCartOverlay>
        <Image url={imageUrl} imageSize={imageSize}>
          {isInstant && !isUnavailable && (
            <InstantAddIndicator>
              <Icon src={PlusCircleIcon} color="white" width="32px" height="32px" />
            </InstantAddIndicator>
          )}
          {isUnavailable && (
            <OutOfStockOverlay imageSize={imageSize}>
              {i18n('Express.Item.Unavailable')}
            </OutOfStockOverlay>
          )}
        </Image>
        <Info>
          <Title>{title}</Title>
          <Price>{price}</Price>
        </Info>
      </Inner>
    </Wrapper>
  );
};

export default Item;
